import { measure } from '@/components/Analytics/Analytics';
import Button from '@/components/Button/Button';
import AddIcon from '@/components/Icon/AddIcon';
import LabelBackground from '@/components/LabelBackground/LabelBackground';
import useCards from '@/hooks/cards/useCards';
import useSpecies from '@/hooks/species/useSpecies';
import { readAndCompressImage } from 'browser-image-resizer';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const ManualUpload: FC<{
	speciesId: number;
}> = ({ speciesId }) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const navigate = useNavigate();

	const { identifyAndStore } = useCards();
	const { findSpeciesById } = useSpecies();

	const species = useMemo(
		() => findSpeciesById(speciesId),
		[findSpeciesById, speciesId],
	);

	const [isUploading, setIsUploading] = useState(false);

	const handleUpload = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			if (!species) return;
			const file = event.target.files?.[0];
			if (!file) return;

			measure(
				`species-manual-upload-photo-${speciesId}-${
					species.attributes.slug
				}`,
			);

			setIsUploading(true);

			readAndCompressImage(file, {
				quality: 0.8,
				maxWidth: 800,
				maxHeight: 800,
			}).then((resizedImage) => {
				// Convert the blob to base64
				const reader = new FileReader();
				reader.readAsDataURL(resizedImage);
				reader.onloadend = () => {
					const image = reader.result;
					identifyAndStore(
						image as string,
						'photo',
						speciesId,
						true,
					).then((result) => {
						setIsUploading(false);
						if (inputRef.current) inputRef.current.value = '';

						// If the light is green, scroll to the top
						if (result?.is_stored) {
							measure(
								`species-manual-upload-photo-identified-success-${
									result.algorithm_step
								}-${speciesId}-${species.attributes.slug}`,
							);

							// Find all elements with overflow-y-auto and scroll to top
							document
								.querySelectorAll('.overflow-y-auto')
								.forEach((el) => {
									el.scrollTop = 0;
								});
						} else if (result?.identified_as) {
							measure(
								`species-manual-upload-photo-identified-error-${speciesId}-${
									species.attributes.slug
								}`,
							);

							// Notify the user of error
							navigate('/oeps', {
								state: {
									image: image,
									targetSpeciesId: speciesId,
									resultIdentifiedAsId: result.species_id,
									resultIdentifiedAsDisplayName:
										result.identified_as,
								},
							});
						} else {
							measure(
								'species-manual-upload-photo-error-connection',
							);

							// Notify the user of error (probably connection error)
							navigate('/oeps', {
								state: {
									image: image,
									targetSpeciesId: speciesId,
								},
							});
						}
					});
				};
			});
		},
		[identifyAndStore, navigate, species, speciesId],
	);

	if (!species) return null;

	return (
		<>
			{/* Hidden input for file upload */}
			<input
				ref={inputRef}
				type="file"
				accept="image/*"
				onChange={handleUpload}
				aria-hidden="true"
				className="hidden"
				tabIndex={-1}
			/>

			{/* Alert screen readers that the photo is being checked */}
			{isUploading && (
				<p role="alert" className="sr-only">
					Foto wordt gecheckt.
				</p>
			)}
			<p className="relative mx-auto mt-16 max-w-xs text-balance p-4 pt-6 text-center text-primary">
				<LabelBackground className="text-white" />
				<span className="relative block pb-2">
					Heb je ergens een foto gemaakt van een{' '}
					{species.attributes.titleShort} zonder internet?
				</span>
				<Button
					label={isUploading ? 'Checken... ' : 'Foto toevoegen'}
					icon={<AddIcon />}
					disabled={isUploading}
					className="relative"
					backgroundClassName={
						isUploading ? 'text-accent' : 'text-primary'
					}
					onClick={() => {
						if (inputRef.current) inputRef.current.click();
					}}
				/>
			</p>
		</>
	);
};

export default ManualUpload;
